import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAttestation, replyToAttestation, addAttestationDocuments, cancelAttestation } from '../../services/attestationApi';
import { dealsApi } from '../../services/api';
import { Badge, Button } from '../ui';

/**
 * AttestationDetailModal — read-only view of an existing attestation request.
 * Opens from AttestationStatusCard.onView so the create fork is never reachable
 * when an attestation already exists.
 *
 * Props:
 *   dealId        {string}
 *   attestationId {string}
 *   onClose       () => void
 */

const FONT = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const STATUS_CONFIG = {
  sent:              { text: 'Attestation sent — awaiting review', badgeVariant: 'primary' },
  under_review:      { text: 'Under review by CPA',               badgeVariant: 'primary' },
  changes_requested: { text: 'Changes requested by CPA',          badgeVariant: 'warning' },
  attested:          { text: 'CPA-attested',                       badgeVariant: 'success' },
  declined:          { text: 'Declined',                           badgeVariant: 'danger'  },
};

const fmtDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date) ? null : date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function AttestationDetailModal({ dealId, attestationId, onClose }) {
  const [detail, setDetail]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [replyText, setReplyText] = useState('');
  const [sending, setSending]   = useState('');    // 'sending' | ''
  const [replyError, setReplyError] = useState('');

  // Add-documents inline control.
  const [addOpen, setAddOpen]         = useState(false);
  const [availDocs, setAvailDocs]     = useState([]);   // [{ id, filename }] not yet attached
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError]     = useState('');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [addingDocs, setAddingDocs]   = useState(false);

  // Cancel-request inline confirmation.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling]       = useState(false);
  const [cancelError, setCancelError]     = useState('');

  const dialogRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchDetail = useCallback(() => {
    getAttestation(dealId, attestationId)
      .then((data) => {
        if (!mountedRef.current) return;
        setDetail(data);
        setLoading(false);
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        setError(e?.response?.data?.error || 'Failed to load attestation details.');
        setLoading(false);
      });
  }, [dealId, attestationId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchDetail();
    return () => { mountedRef.current = false; };
  }, [fetchDetail]);

  // Focus the dialog container on mount for a11y.
  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
  }, []);

  // Escape key closes the modal.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending('sending');
    setReplyError('');
    try {
      const msg = await replyToAttestation(dealId, attestationId, replyText.trim());
      if (!mountedRef.current) return;
      // Optimistically append or refetch.
      setDetail((prev) => prev
        ? { ...prev, messages: [...(prev.messages || []), msg] }
        : prev
      );
      setReplyText('');
    } catch (e) {
      if (!mountedRef.current) return;
      setReplyError(e?.response?.data?.error || 'Failed to send reply.');
    } finally {
      if (mountedRef.current) setSending('');
    }
  };

  // Open the add-documents control and fetch deal docs not already attached.
  const openAddDocs = () => {
    setAddOpen(true);
    setDocsError('');
    setSelectedDocs(new Set());
    setDocsLoading(true);
    const attachedIds = new Set((detail?.doc_manifest || []).map((d) => d.document_id));
    dealsApi.getDocuments(dealId)
      .then((r) => {
        if (!mountedRef.current) return;
        const docs = (Array.isArray(r.data) ? r.data : [])
          .filter((d) => d && d.id != null && !attachedIds.has(d.id))
          .map((d) => ({ id: d.id, filename: d.filename }));
        setAvailDocs(docs);
      })
      .catch(() => {
        if (mountedRef.current) setDocsError('Failed to load deal documents.');
      })
      .finally(() => { if (mountedRef.current) setDocsLoading(false); });
  };

  const toggleSelectedDoc = (id) =>
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = availDocs.length > 0 && selectedDocs.size === availDocs.length;

  const toggleSelectAll = () =>
    setSelectedDocs(allSelected ? new Set() : new Set(availDocs.map((d) => d.id)));

  const submitAddDocs = async () => {
    if (selectedDocs.size === 0) return;
    setAddingDocs(true);
    setDocsError('');
    try {
      await addAttestationDocuments(dealId, attestationId, Array.from(selectedDocs));
      if (!mountedRef.current) return;
      setAddOpen(false);
      setSelectedDocs(new Set());
      fetchDetail();
    } catch (e) {
      if (mountedRef.current) setDocsError(e?.response?.data?.error || 'Failed to add documents.');
    } finally {
      if (mountedRef.current) setAddingDocs(false);
    }
  };

  const cancelRequest = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      await cancelAttestation(dealId, attestationId);
      if (!mountedRef.current) return;
      setConfirmCancel(false);
      fetchDetail();
    } catch (e) {
      if (mountedRef.current) setCancelError(e?.response?.data?.error || 'Failed to cancel the request.');
    } finally {
      if (mountedRef.current) setCancelling(false);
    }
  };

  const isCancelled = detail && detail.status === 'declined' && detail.declined_reason === 'CANCELLED_BY_DEAL_TEAM';
  const cfg = detail
    ? (isCancelled
        ? { text: 'Cancelled', badgeVariant: 'default' }
        : (STATUS_CONFIG[detail.status] || { text: detail.status, badgeVariant: 'default' }))
    : null;
  const isTerminal = detail && (detail.status === 'attested' || detail.status === 'declined');

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,24,39,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Attestation details"
        tabIndex={-1}
        style={{
          background: '#FFFFFF',
          borderRadius: 16,
          width: 600,
          maxWidth: '92vw',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid #E5E7EB',
          fontFamily: FONT,
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '24px 28px',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
              CPA Attestation
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
              Read-only view of the current attestation request.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9CA3AF',
              fontSize: 18,
              lineHeight: 1,
              padding: '2px 4px',
              borderRadius: 4,
              transition: 'color 0.15s ease',
              marginLeft: 16,
              flexShrink: 0,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#111827'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px' }}>
          {loading && (
            <div style={{ color: '#6B7280', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
              Loading…
            </div>
          )}

          {error && !loading && (
            <div style={{
              background: '#F8D7DA',
              border: '1px solid #F1AEB5',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 13,
              color: '#DC3545',
            }}>
              {error}
            </div>
          )}

          {detail && !loading && (
            <>
              {/* Status badge */}
              <div style={{ marginBottom: 20 }}>
                <Badge
                  variant={cfg.badgeVariant}
                  size="sm"
                  style={{
                    textTransform: 'none', letterSpacing: 'normal', fontWeight: 600, fontSize: 12,
                    ...(isCancelled ? { backgroundColor: '#F3F4F6', color: '#6B7280' } : {}),
                  }}
                >
                  {cfg.text}
                  {detail.status === 'attested' && detail.attested_at
                    ? ` (${new Date(detail.attested_at).toLocaleDateString()})`
                    : ''}
                </Badge>
              </div>

              {/* CPA info */}
              <InfoBlock>
                <InfoRow label="CPA name"  value={detail.cpa_name  || '—'} />
                <InfoRow label="CPA firm"  value={detail.cpa_firm  || '—'} />
                <InfoRow label="CPA email" value={detail.cpa_email || '—'} />
                {detail.sent_at     && <InfoRow label="Sent"     value={fmtDate(detail.sent_at)}     />}
                {detail.attested_at && <InfoRow label="Attested" value={fmtDate(detail.attested_at)} />}
                {detail.status === 'declined' && detail.declined_reason && (
                  <InfoRow label="Reason" value={detail.declined_reason} />
                )}
              </InfoBlock>

              {/* Supporting documents */}
              {detail.doc_manifest && detail.doc_manifest.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <SectionLabel>Supporting documents ({detail.doc_manifest.length})</SectionLabel>
                  <div style={{
                    background: '#F8F9FA',
                    borderRadius: 10,
                    border: '1px solid #E5E7EB',
                    overflow: 'hidden',
                  }}>
                    {detail.doc_manifest.map((d, i) => (
                      <div
                        key={d.document_id || i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          maxWidth: '100%',
                          padding: '8px 14px',
                          borderBottom: i < detail.doc_manifest.length - 1 ? '1px solid #E5E7EB' : 'none',
                          fontSize: 13,
                        }}
                      >
                        <span
                          title={d.filename || 'Untitled document'}
                          style={{ color: '#374151', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {d.filename || 'Untitled document'}
                        </span>
                        {d.sha256 && (
                          <span style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace', whiteSpace: 'nowrap', flex: 'none' }}>
                            sha256 {d.sha256.slice(0, 12)}…
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add documents — inline control, hidden when terminal */}
              {!isTerminal && (
                <div style={{ marginBottom: 20 }}>
                  {!addOpen ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openAddDocs}
                      style={{ borderColor: '#0D6EFD', color: '#0D6EFD' }}
                    >
                      + Add documents
                    </Button>
                  ) : (
                    <div style={{
                      background: '#F8F9FA',
                      borderRadius: 10,
                      border: '1px solid #E5E7EB',
                      padding: '14px 16px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <SectionLabel>Add documents</SectionLabel>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                          {!docsLoading && availDocs.length > 0 && (
                            <button
                              onClick={toggleSelectAll}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: '#0D6EFD', fontSize: 13, fontWeight: 600, padding: 0,
                              }}
                            >
                              {allSelected ? 'Clear' : 'Select all'}
                            </button>
                          )}
                          <button
                            onClick={() => { setAddOpen(false); setDocsError(''); }}
                            aria-label="Cancel adding documents"
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#9CA3AF', fontSize: 13, padding: 0,
                              transition: 'color 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#111827'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#9CA3AF'; }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>

                      {docsError && (
                        <div style={{
                          background: '#F8D7DA',
                          border: '1px solid #F1AEB5',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 13,
                          color: '#DC3545',
                          marginBottom: 10,
                        }}>
                          {docsError}
                        </div>
                      )}

                      {docsLoading ? (
                        <div style={{ fontSize: 13, color: '#9CA3AF', padding: '6px 0' }}>Loading…</div>
                      ) : availDocs.length === 0 ? (
                        <div style={{ fontSize: 13, color: '#9CA3AF', padding: '6px 0' }}>
                          All deal documents are already attached.
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4, marginBottom: 12 }}>
                            {availDocs.map((d) => (
                              <label
                                key={d.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  maxWidth: '100%',
                                  padding: '6px 4px',
                                  fontSize: 13,
                                  color: '#374151',
                                  cursor: 'pointer',
                                  borderRadius: 6,
                                  transition: 'background 0.12s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDocs.has(d.id)}
                                  onChange={() => toggleSelectedDoc(d.id)}
                                  style={{ accentColor: '#0D6EFD', width: 15, height: 15, flex: 'none' }}
                                />
                                <span
                                  title={d.filename}
                                  style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {d.filename}
                                </span>
                              </label>
                            ))}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="outline"
                              size="md"
                              onClick={submitAddDocs}
                              isDisabled={addingDocs || selectedDocs.size === 0}
                              isLoading={addingDocs}
                              style={{ background: '#0D6EFD', color: '#FFFFFF', borderColor: '#0D6EFD' }}
                            >
                              {addingDocs
                                ? 'Adding…'
                                : selectedDocs.size > 0
                                  ? `Add ${selectedDocs.size} document${selectedDocs.size === 1 ? '' : 's'}`
                                  : 'Add documents'}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Q&A thread */}
              <div style={{ marginBottom: isTerminal ? 0 : 16 }}>
                <SectionLabel>
                  Q&amp;A thread ({(detail.messages || []).length})
                </SectionLabel>
                {(!detail.messages || detail.messages.length === 0) ? (
                  <div style={{ fontSize: 13, color: '#9CA3AF', padding: '6px 0' }}>No messages yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 10, marginBottom: isTerminal ? 0 : 12 }}>
                    {detail.messages.map((m, i) => {
                      const isCpa = m.sender_type === 'cpa';
                      return (
                        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isCpa ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            maxWidth: '85%',
                            background: isCpa ? '#E7F1FF' : '#F3F4F6',
                            borderRadius: 10,
                            padding: '8px 12px',
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: isCpa ? '#0D6EFD' : '#6B7280', marginBottom: 2 }}>
                              {m.sender_name || (isCpa ? 'CPA' : 'Deal team')}
                            </div>
                            <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                              {m.content}
                            </div>
                          </div>
                          {m.created_at && (
                            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2, padding: '0 4px' }}>
                              {new Date(m.created_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Reply composer — hidden when terminal status */}
                {!isTerminal && (
                  <div style={{ marginTop: 10 }}>
                    {replyError && (
                      <div style={{
                        background: '#F8D7DA',
                        border: '1px solid #F1AEB5',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 13,
                        color: '#DC3545',
                        marginBottom: 8,
                      }}>
                        {replyError}
                      </div>
                    )}
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Reply to the CPA…"
                      rows={3}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '10px 14px',
                        border: '1.5px solid #E5E7EB',
                        borderRadius: 8,
                        fontSize: 14,
                        color: '#111827',
                        fontFamily: FONT,
                        resize: 'vertical',
                        outline: 'none',
                        transition: 'border-color 0.15s ease',
                        marginBottom: 8,
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#0D6EFD'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outline"
                        size="md"
                        onClick={sendReply}
                        isDisabled={sending === 'sending' || !replyText.trim()}
                        isLoading={sending === 'sending'}
                        style={{ background: '#0D6EFD', color: '#FFFFFF', borderColor: '#0D6EFD' }}
                      >
                        {sending === 'sending' ? 'Sending…' : 'Send'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Legal watermark + cancel control */}
              <div style={{
                marginTop: 22,
                borderTop: '1px solid #E5E7EB',
                paddingTop: 14,
              }}>
                <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.5 }}>
                  DRAFT — UNATTESTED — prepared by DealNexa platform. The CPA's signed letter is the attested deliverable.
                </div>

                {/* Cancel request — only while the request is still active. */}
                {!isTerminal && (
                  <div style={{ marginTop: 14 }}>
                    {cancelError && (
                      <div style={{
                        background: '#F8D7DA',
                        border: '1px solid #F1AEB5',
                        borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 13,
                        color: '#DC3545',
                        marginBottom: 10,
                      }}>
                        {cancelError}
                      </div>
                    )}
                    {!confirmCancel ? (
                      <button
                        onClick={() => { setConfirmCancel(true); setCancelError(''); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#DC3545', fontSize: 13, fontWeight: 500, padding: 0,
                          transition: 'opacity 0.15s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                      >
                        Cancel request
                      </button>
                    ) : (
                      <div style={{
                        background: '#FFF5F5',
                        border: '1px solid #F1AEB5',
                        borderRadius: 8,
                        padding: '12px 14px',
                      }}>
                        <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.5, marginBottom: 10 }}>
                          Cancel this attestation request? The CPA will no longer be able to review it.
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelRequest}
                            isDisabled={cancelling}
                            isLoading={cancelling}
                            style={{ background: '#DC3545', color: '#FFFFFF', borderColor: '#DC3545' }}
                          >
                            {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmCancel(false)}
                            isDisabled={cancelling}
                          >
                            Keep it
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ children }) {
  return (
    <div style={{
      background: '#F8F9FA',
      borderRadius: 10,
      border: '1px solid #E5E7EB',
      padding: '12px 16px',
      marginBottom: 20,
      display: 'grid',
      gap: 6,
    }}>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
      <span style={{ color: '#6B7280', fontWeight: 500, minWidth: 88, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#111827' }}>{value}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 12,
      fontWeight: 600,
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      marginBottom: 8,
    }}>
      {children}
    </div>
  );
}
